<template>
  <ApplicationBase
    v-bind="vBindBase"
    color="green"
    applicationType="rent"
    v-on="vOnBase"
  >

    <template #apartments>
      <ApplicationApartments
        v-bind="vBindApartments"
        color="green"
        v-on="vOnApartments"
        apartmentTypeSearchQueryValue="rental"
      />
    </template>

    <div class="container container--narrow">
      <div class="application-section-bg">
        <ApplicationApplicants
          v-bind="vBindApplicants"
          @update="applicants = $event"
          v-slot="{vApplicant, index}"
          applicationType="rent"
        >

          <div class="form__row">
            <InputWrap
              class="form__col-6"
              data-cy="monthly-income"
              :label="translate('Kuukausitulot (brutto)')"
              :validations="vApplicant.GrossIncome"
              v-slot="{error}"
            >
              <InputText
                v-model="vApplicant.GrossIncome.$model"
                :disabled="preview"
                :error="error"
              >
                €
              </InputText>
            </InputWrap>

            <InputWrap
              v-if="index === 0"
              class="form__col-6"
              data-cy="financial-status"
              :label="translate('Koko talouden varallisuus')"
              :validations="$v.form.TotalWealth"
              v-slot="{error}"
            >
              <InputText
                v-model="$v.form.TotalWealth.$model"
                :disabled="preview"
                :error="error"
              >
                €
              </InputText>
            </InputWrap>
          </div>

          <div class="form__row">
            <InputWrap
              class="form__col-6"
              data-cy="profession"
              :label="translate('Ammatti')"
              :validations="vApplicant.Occupation"
              v-slot="{error}"
            >
              <InputText
                v-model="vApplicant.Occupation.$model"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>

            <InputWrap
              class="form__col-6"
              data-cy="employer"
              :label="translate('Työpaikka')"
              :validations="vApplicant.Employer"
              v-slot="{error}"
            >
              <InputText
                v-model="vApplicant.Employer.$model"
                :disabled="preview"
                :error="error"
              />
            </InputWrap>
          </div>

          <div class="form__row">
            <InputWrap
              class="form__col-6"
              data-cy="employment-start-date"
              :label="translate('Työsuhde alkoi')"
              :validations="vApplicant.JobStartDate"
              :wrap-label="false"
              v-slot="{id, error}"
            >
              <InputDate
                :id="id"
                :error="error"
                v-model="vApplicant.JobStartDate.$model"
                :disabled="preview"
              />
            </InputWrap>
          </div>

        </ApplicationApplicants>
      </div><!-- application-section-bg -->
    </div><!-- container container--narrow -->

    <ApplicationDivider />

    <ApplicationOtherResidents
      :items="form.OtherResidents"
      :item-init="createOtherResident"
      :v-iter="$v.form.OtherResidents.$each.$iter"
      :preview="preview"
      @update="form.OtherResidents = $event"
    />

    <div class="container container--narrow">
      <ApplicationSection
        :title="translate('Asunnon tarve')"
        wrapper-classes="application-section-bg mb-ts-2">
        <div class="form">
          <div class="form__grid">
            <div class="form__row">
              <InputWrap
                class="form__col-6"
                data-cy="reason-for-apartment"
                :label="translate('Asunnon tarpeen syy')"
                :validations="$v.form.ApplicationReason"
                :wrap-label="false"
                v-slot="{id, error}"
              >
                <InputSelect
                  v-model="$v.form.ApplicationReason.$model"
                  :id="id"
                  :error="error"
                  :options="optionsReason"
                  :disabled="preview"
                  simple-value
                />
              </InputWrap>

              <InputWrap
                class="form__col-6"
                data-cy="need-for-apartment-start-date"
                :label="translate('Asunnon tarve alkaen')"
                :validations="$v.form.NeedForApartmentStartDate"
                :wrap-label="false"
                v-slot="{id, error}"
              >
                <InputDate
                  v-model="$v.form.NeedForApartmentStartDate.$model"
                  :disabled="preview"
                  :id="id"
                  :error="error"
                />
              </InputWrap>
            </div>

            <div class="form__row">
              <InputWrap
                class="form__col"
                data-cy="application-reason-additional-info"
                :label="translate('Asunnon tarpeen lisätiedot')"
                :validations="$v.form.ApplicationReasonAdditionalInfo"
                v-slot="{error}"
              >
                <InputTextarea
                  v-model="$v.form.ApplicationReasonAdditionalInfo.$model"
                  :preview="preview"
                  :error="error"
                />
              </InputWrap>
            </div>
          </div>
        </div>
      </ApplicationSection>
    </div>

    <div class="container container--narrow">
      <ApplicationSection
        :title="translate('Nykyisen asunnon tiedot')"
        wrapper-classes="application-section-bg mb-ts-2"
      >
        <div class="form" id="current-apartment-info">
          <div class="form__grid">
            <div class="form__row">
              <InputWrap
                class="form__col-6"
                data-cy="type-of-apartment"
                :label="translate('Asumismuoto')"
                :validations="$v.form.CurrentApartmentInfo.AccommodationType"
                :wrap-label="false"
                v-slot="{id, error}"
              >
                <InputSelect
                  v-model="$v.form.CurrentApartmentInfo.AccommodationType.$model"
                  :id="id"
                  :error="error"
                  :options="optionsCurrentAccommodationType"
                  :disabled="preview"
                  simple-value
                />
              </InputWrap>

              <InputWrap
                class="form__col-6"
                data-cy="number-of-rooms"
                :label="translate('Huoneita')"
                :validations="$v.form.CurrentApartmentInfo.ApartmentType"
                :wrap-label="false"
                v-slot="{id, error}"
              >
                <InputSelect
                  v-model="$v.form.CurrentApartmentInfo.ApartmentType.$model"
                  :id="id"
                  :error="error"
                  :options="optionsCurrentApartmentType"
                  :disabled="preview"
                  simple-value
                />
              </InputWrap>
            </div>

            <div class="form__row">
              <InputWrap
                class="form__col-6"
                data-cy="area"
                :label="translate('Pinta-ala')"
                :validations="$v.form.CurrentApartmentInfo.Area"
                v-slot="{error}"
              >
                <InputText
                  v-model="$v.form.CurrentApartmentInfo.Area.$model"
                  :disabled="preview"
                  :error="error"
                >
                  m²
                </InputText>
              </InputWrap>

              <InputWrap
                class="form__col-6"
                data-cy="number-of-residents"
                :label="translate('Asukkaiden lukumäärä')"
                :validations="$v.form.CurrentApartmentInfo.NumberOfResidents"
                v-slot="{error}"
              >
                <InputText
                  v-model="$v.form.CurrentApartmentInfo.NumberOfResidents.$model"
                  :disabled="preview"
                  :error="error"
                />
              </InputWrap>
            </div>

            <div class="form__row">
              <InputWrap
                class="form__col-6"
                data-cy="rent-amount"
                :label="translate('Vuokra')"
                :validations="$v.form.CurrentApartmentInfo.Rent"
                v-slot="{error}"
              >
                <InputText
                  v-model="$v.form.CurrentApartmentInfo.Rent.$model"
                  :disabled="preview"
                  :error="error"
                >
                  €
                </InputText>
              </InputWrap>

              <InputWrap
                class="form__col-6"
                data-cy="maintenance-charge"
                :label="translate('Vastike')"
                :validations="$v.form.CurrentApartmentInfo.MaintenanceCharge"
                v-slot="{error}"
              >
                <InputText
                  v-model="$v.form.CurrentApartmentInfo.MaintenanceCharge.$model"
                  :disabled="preview"
                  :error="error"
                >
                  €
                </InputText>
              </InputWrap>
            </div>
          </div>
        </div>
      </ApplicationSection>
    </div>

    <input type="hidden" name="Checksum" :value=form.Checksum />

  </ApplicationBase>
</template>

<script>
  import {
    required,
    maxLength,
    integer,
    minValue,
    maxValue,
  } from 'vuelidate/lib/validators';

  import {
    validations,
    extendApplicantCreator,
  } from '../lib/applicationShared';
  import application from './mixins/application';
  import globals from '../lib/globals';

  const mapApplicantExtend = (applicant) => ({
    GrossIncome: applicant.GrossIncome,
    Occupation: applicant.Occupation,
    Employer: applicant.Employer,
    JobStartDate: applicant.JobStartDate,
  });

  export default {
    mixins: [
      application({
        favouritesType: 'rental',
        presetMapExtend: {
          mapApplicant: mapApplicantExtend,
        },
        localSaveExtend: {
          localSaveFromStateForm(state) {
            return {
              TotalWealth: state.form.TotalWealth,
              ApplicationReason: state.form.ApplicationReason,
              NeedForApartmentStartDate: state.form.NeedForApartmentStartDate,
              ApplicationReasonAdditionalInfo: state.form.ApplicationReasonAdditionalInfo,
              CurrentApartmentInfo: state.form.CurrentApartmentInfo,
            };
          },
          localSaveFromStateApplicant: mapApplicantExtend,
          stateFromLocalSave(state, localSave) {
            state.form.TotalWealth = localSave.form.TotalWealth;
            state.form.ApplicationReason = localSave.form.ApplicationReason;
            state.form.NeedForApartmentStartDate = localSave.form.NeedForApartmentStartDate;
            state.form.ApplicationReasonAdditionalInfo = localSave.form.ApplicationReasonAdditionalInfo;
            state.form.CurrentApartmentInfo = localSave.form.CurrentApartmentInfo;
          },
        },
        applyOtherData: {
          conditionFavouritesType: 'right-of-occupancy',
          title: 'Korissasi on myös ASO-asuntoja',
          ctaText: 'Jätä ASO-asuntohakemus',
          ctaUrl: globals.urlApplicationRightOfOccupancy,
          color: 'pink',
        },
      }),
    ],
    data() {
      const createOptions = (labels, translate) => labels.map((label, index) => ({
        label: translate ? this.translate(label) : label,
        value: (index + 1).toString(),
      }));
      return {
        optionsReason: createOptions([
          'Asunnoton',
          'Muuttovelvoite nykyisestä asunnosta',
          'Liian pieni asunto',
          'Vuokran suuruus',
          'Paikkakunnan vaihto',
          'Muu syy',
          'Terveydelliset syyt',
          'Liian suuri asunto',
          'Avio- / avoliitto',
          'Avio- / avoero',
          'Asunnon huono kunto',
          'Epätyydyttävä asunto',
          'Itsenäistyminen',
        ], true),
        optionsCurrentAccommodationType: createOptions([
          'Asumisoikeusasunto',
          'Vuokra-asunto',
          'Omistusasunto',
          'Osaomistusasunto',
          'Alivuokra-asunto',
          'Asun vanhempien luona',
          'Työsuhdeasunto',
          'Opiskelija-asunto',
        ], true),
        optionsCurrentApartmentType: createOptions([
          1,
          2,
          3,
          4,
          '5+',
        ]),
        form: {
          Type: '2',
          TotalWealth: '',
          ApplicationReason: null,
          NeedForApartmentStartDate: null,
          ApplicationReasonAdditionalInfo: '',
          CurrentApartmentInfo: {
            AccommodationType: null,
            ApartmentType: null,
            Area: '',
            NumberOfResidents: '',
            Rent: '',
            MaintenanceCharge: '',
          },
        },
      };
    },
    validations() {
      return {
        applicants: {
          $each: {
            ...validations.applicant,
            ...{
              GrossIncome: {
                required,
                integer,
                minValue: minValue(0),
                maxValue: maxValue(100000),
              },
              Occupation: {
                required,
                maxLength: maxLength(255),
              },
              Employer: {
                required,
                maxLength: maxLength(100),
              },
              JobStartDate: {},
            },
          },
        },
        form: {
          ...validations.form,
          TotalWealth: {
            required,
            integer,
            minValue: minValue(0),
            maxValue: maxValue(50000000),
          },
          ApplicationReason: {
            required,
          },
          NeedForApartmentStartDate: {
            required,
          },
          ApplicationReasonAdditionalInfo: {
            maxLength: maxLength(2000),
          },
          CurrentApartmentInfo: {
            ApartmentType: {
              required,
            },
            AccommodationType: {
              required,
            },
            Area: {
              integer,
            },
            NumberOfResidents: {
              integer,
            },
            Rent: {
              integer,
              minValue: minValue(0),
              maxValue: maxValue(10000),
            },
            MaintenanceCharge: {
              integer,
              minValue: minValue(0),
              maxValue: maxValue(10000),
            },
          },
        },
      };
    },
    methods: {
      createApplicant: extendApplicantCreator({
        GrossIncome: '',
        Occupation: '',
        Employer: '',
        JobStartDate: null,
      }),
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";
</style>
